import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { api, getPropertyDocumentDownloadUrl } from '../../services/api';
import { Card } from '../../components/ui/Card';
import { Table, Column } from '../../components/ui/Table';
import {
  formatRentPeriod,
  RENT_PAYMENT_STATUS_LABEL,
  TENANT_PAYMENT_KIND_LABEL,
  ymToSpanishLabel,
} from '../../utils/rentPaymentDisplay';
import { formatDate, formatDateTime, todayLocalDateString } from '../../utils/date';

type Toast = { message: string; type: 'success' | 'error' };

interface TenantPaymentsTabProps {
  tenantId: string;
  canUpdateRentals: boolean;
  setToast: (t: Toast | null) => void;
  /** Tras registrar un pago (p. ej. refrescar checklist si el backend marcó «Pagado» automático). */
  onPaymentRecorded?: () => void | Promise<void>;
}

const RECEIPT_MAX_BYTES = 12 * 1024 * 1024;

function receiptHref(url: string | null | undefined): string {
  if (!url?.trim()) return '';
  const s = url.trim();
  if (s.startsWith('data:') || s.startsWith('http://') || s.startsWith('https://')) return s;
  return getPropertyDocumentDownloadUrl(s);
}

function formatRowPaymentDate(r: { paymentDate?: string; periodYear: number; periodMonth: number }): string {
  if (r.paymentDate) {
    const d = formatDate(r.paymentDate);
    if (d !== '—') return d;
  }
  return formatRentPeriod(r.periodYear, r.periodMonth);
}

export const TenantPaymentsTab: React.FC<TenantPaymentsTabProps> = ({
  tenantId,
  canUpdateRentals,
  setToast,
  onPaymentRecorded,
}) => {
  const queryClient = useQueryClient();
  const [paymentDate, setPaymentDate] = useState(todayLocalDateString());
  const [transactionKind, setTransactionKind] = useState<'rent' | 'deposit'>('rent');
  const [status, setStatus] = useState<'paid' | 'unpaid' | 'partial'>('paid');
  const [paidAmount, setPaidAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [receiptUrl, setReceiptUrl] = useState('');
  const [receiptName, setReceiptName] = useState('');
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['tenant-payment-state', tenantId],
    queryFn: () => api.getTenantPaymentState(tenantId),
    enabled: !!tenantId,
    staleTime: 15_000,
  });

  const handleReceiptFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > RECEIPT_MAX_BYTES) {
      setToast({ message: 'El archivo supera 12 MB. Elige uno más pequeño.', type: 'error' });
      return;
    }
    setReceiptUploading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setReceiptUploading(false);
      const result = reader.result;
      if (typeof result === 'string') {
        setReceiptUrl(result);
        setReceiptName(file.name);
      }
    };
    reader.onerror = () => {
      setReceiptUploading(false);
      setToast({ message: 'No se pudo leer el archivo.', type: 'error' });
    };
    reader.readAsDataURL(file);
  };

  const clearReceipt = () => {
    setReceiptUrl('');
    setReceiptName('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedPaidAmount = paidAmount.trim() === '' ? null : Number(paidAmount.replace(',', '.'));
    const isRent = transactionKind === 'rent';
    const isPartial = status === 'partial';
    if (isRent && isPartial && (normalizedPaidAmount == null || Number.isNaN(normalizedPaidAmount) || normalizedPaidAmount <= 0)) {
      setToast({ message: 'Para pago parcial debes capturar un monto pagado mayor a 0.', type: 'error' });
      return;
    }
    if (isRent && normalizedPaidAmount != null && (Number.isNaN(normalizedPaidAmount) || normalizedPaidAmount < 0)) {
      setToast({ message: 'El monto pagado debe ser un número válido mayor o igual a 0.', type: 'error' });
      return;
    }
    try {
      setSubmitting(true);
      await api.createTenantMonthlyPayment(tenantId, {
        paymentDate,
        transactionKind,
        status,
        notes: notes.trim() || undefined,
        receiptUrl: receiptUrl.trim() || undefined,
        paidAmount: isRent && normalizedPaidAmount != null ? normalizedPaidAmount : undefined,
      });
      setToast({ message: 'Registro guardado.', type: 'success' });
      setNotes('');
      setPaidAmount('');
      clearReceipt();
      await queryClient.invalidateQueries({ queryKey: ['tenant-payment-state', tenantId] });
      await queryClient.invalidateQueries({ queryKey: ['tenant-payments-summary'] });
      await queryClient.invalidateQueries({ queryKey: ['tenant-timeline', tenantId] });
      await queryClient.invalidateQueries({ queryKey: ['change-logs', 'TENANT', tenantId] });
      await onPaymentRecorded?.();
    } catch (err: any) {
      setToast({ message: err.message || 'Error al guardar', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  type HistRow = NonNullable<typeof data>['history'][number];

  const historyColumns: Column<HistRow>[] = [
    {
      header: 'Registrado',
      accessor: (r) => formatDateTime(r.createdAt),
    },
    {
      header: 'Fecha de pago',
      accessor: (r) => formatRowPaymentDate(r),
    },
    {
      header: 'Tipo',
      accessor: (r) => (
        <span className="text-sm text-gray-800">
          {TENANT_PAYMENT_KIND_LABEL[r.transactionKind || 'rent'] ?? r.transactionKind ?? '—'}
        </span>
      ),
    },
    {
      header: 'Mes calendario',
      accessor: (r) => formatRentPeriod(r.periodYear, r.periodMonth),
    },
    {
      header: 'Renta pactada',
      accessor: () =>
        typeof data?.monthlyRent === 'number'
          ? `$${Number(data.monthlyRent).toLocaleString('es-MX')}`
          : '—',
    },
    {
      header: 'Monto pagado',
      accessor: (r) => {
        const isRent = (r.transactionKind || 'rent') === 'rent';
        if (!isRent) return <span className="text-gray-400">—</span>;
        if (r.paidAmount == null) return <span className="text-gray-400">—</span>;
        return <span className="font-medium text-gray-900">${Number(r.paidAmount).toLocaleString('es-MX')}</span>;
      },
    },
    {
      header: 'Estado',
      accessor: (r) => (
        <span
          className={`px-2 py-0.5 rounded text-xs font-medium ${
            r.status === 'paid'
              ? 'bg-green-100 text-green-800'
              : r.status === 'unpaid'
                ? 'bg-red-100 text-red-800'
                : 'bg-amber-100 text-amber-900'
          }`}
        >
          {RENT_PAYMENT_STATUS_LABEL[r.status] ?? r.status}
        </span>
      ),
    },
    {
      header: 'Comprobante',
      accessor: (r) => {
        const href = receiptHref(r.receiptUrl);
        if (!href) return <span className="text-gray-400 text-sm">—</span>;
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center p-1.5 rounded-lg text-primary hover:bg-primary/10"
            title="Ver comprobante"
            aria-label="Ver comprobante"
          >
            <FileText className="w-4 h-4 shrink-0" />
          </a>
        );
      },
    },
    {
      header: 'Notas',
      accessor: (r) => <span className="text-sm text-gray-700 whitespace-pre-wrap">{r.notes || '—'}</span>,
    },
  ];

  if (isLoading) {
    return (
      <div className="bg-white rounded-b-lg border border-t-0 border-gray-200 p-6">
        <p className="text-gray-500">Cargando pagos…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-b-lg border border-t-0 border-gray-200 p-6">
        <p className="text-red-600">{(error as Error).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-white rounded-b-lg border border-t-0 border-gray-200 p-6">
      <Card title="Estado de pagos">
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-gray-600">¿Tiene atraso?</span>
            {data?.hasArrears ? (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-900">Sí</span>
            ) : (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">No</span>
            )}
          </div>
          <div>
            <span className="text-gray-600">Meses con no pago o pago parcial (hasta el mes actual): </span>
            {data?.unpaidMonthLabels && data.unpaidMonthLabels.length > 0 ? (
              <span className="font-medium text-gray-900">
                {data.unpaidMonthLabels.map(ymToSpanishLabel).join(', ')}
              </span>
            ) : (
              <span className="text-gray-500">Ninguno registrado</span>
            )}
          </div>
          <p className="text-xs text-gray-500">
            El atraso se calcula solo con movimientos de tipo <strong>Pago de renta</strong>. La fecha que eliges
            define el día del pago; el mes de renta que se actualiza es el <strong>mes calendario</strong> de esa fecha.
            Los <strong>depósitos</strong> quedan en el historial pero no cambian el estado mensual de la renta.
          </p>
        </div>
      </Card>

      {canUpdateRentals && (
        <Card title="Registrar pago o depósito">
          <form onSubmit={handleSubmit} className="space-y-4 max-w-xl">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha del pago o movimiento <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900"
              />
              <p className="text-xs text-gray-500 mt-1">
                Día en que se realizó el pago. Para renta, el periodo que se registra es el mes de esta fecha.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <select
                value={transactionKind}
                onChange={(e) => {
                  const k = e.target.value as 'rent' | 'deposit';
                  setTransactionKind(k);
                  if (k === 'deposit') {
                    setStatus('paid');
                    setPaidAmount('');
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900"
              >
                <option value="rent">{TENANT_PAYMENT_KIND_LABEL.rent}</option>
                <option value="deposit">{TENANT_PAYMENT_KIND_LABEL.deposit}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select
                value={status}
                onChange={(e) => {
                  const nextStatus = e.target.value as 'paid' | 'unpaid' | 'partial';
                  setStatus(nextStatus);
                  if (nextStatus === 'unpaid') {
                    setPaidAmount('0');
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900"
              >
                <option value="paid">{RENT_PAYMENT_STATUS_LABEL.paid}</option>
                <option value="unpaid">{RENT_PAYMENT_STATUS_LABEL.unpaid}</option>
                <option value="partial">{RENT_PAYMENT_STATUS_LABEL.partial}</option>
              </select>
            </div>
            {transactionKind === 'rent' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monto pagado {status === 'partial' ? <span className="text-red-500">*</span> : '(opcional)'}
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  required={status === 'partial'}
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900"
                  placeholder={
                    status === 'paid'
                      ? 'Si lo dejas vacío, se usa la renta pactada'
                      : status === 'partial'
                      ? 'Captura el monto recibido'
                      : '0'
                  }
                />
                <p className="text-xs text-gray-500 mt-1">
                  Renta pactada actual:{' '}
                  {typeof data?.monthlyRent === 'number'
                    ? `$${Number(data.monthlyRent).toLocaleString('es-MX')}`
                    : '—'}
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Comprobante (opcional)</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,image/*,application/pdf"
                disabled={receiptUploading}
                onChange={handleReceiptFile}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-accent file:text-white hover:file:bg-accent/90 disabled:opacity-50"
              />
              {receiptUploading && <p className="text-xs text-gray-500 mt-1">Leyendo archivo…</p>}
              {receiptUrl && !receiptUploading && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-green-700">{receiptName || 'Archivo listo'}</span>
                  <button
                    type="button"
                    onClick={clearReceipt}
                    className="text-red-600 hover:underline text-xs"
                  >
                    Quitar
                  </button>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">PDF o imagen, máx. 12 MB.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas (opcional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm"
                placeholder="Referencia bancaria, acuerdos…"
              />
            </div>
            <button
              type="submit"
              disabled={submitting || receiptUploading}
              className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50 text-sm font-medium"
            >
              {submitting ? 'Guardando…' : 'Guardar registro'}
            </button>
          </form>
        </Card>
      )}

      <Card title="Historial de registros">
        {data?.history && data.history.length > 0 ? (
          <div className="overflow-x-auto">
            <Table columns={historyColumns} data={data.history} />
          </div>
        ) : (
          <p className="text-sm text-gray-500 py-4">Aún no hay registros de pago para este inquilino.</p>
        )}
      </Card>
    </div>
  );
};
